<script>
    import Knob from "./Knob.svelte";
    import AudioBars from "./AudioBars.svelte";
    import AudioWave from "./AudioWave.svelte";
    import AudioCursor from "./AudioCursor.svelte";
    import AudioTime from "./AudioTime.svelte";
    import AudioButton from "./AudioButton.svelte";
    import {
        current_episode,
        episodes,
        episode_desc,
        paused,
        current_time,
    } from "../stores/store.js";

    export let episode;
    export let isRadio = false;

    let waveData = null;
    let isLoadingWave = false;
    const waveCache = new Map();

    $: if (isRadio || !episode?.id) {
        waveData = null;
        isLoadingWave = false;
    } else {
        loadWaveData(episode.id);
    }

    async function loadWaveData(episodeId) {
        if (waveCache.has(episodeId)) {
            waveData = waveCache.get(episodeId);
            isLoadingWave = false;
            return;
        }

        waveData = null;
        isLoadingWave = true;

        try {
            const res = await fetch(`/api/episode/${episodeId}/audiowave`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            if (episode?.id !== episodeId) return;

            let parsed = null;
            if (data && typeof data === "object" && "audiowave" in data) {
                parsed = JSON.parse(data.audiowave);
            }

            waveCache.set(episodeId, parsed);
            waveData = parsed;
        } catch (err) {
            console.error(
                `Failed to load audiowave for episode ${episodeId}:`,
                err,
            );
            if (episode?.id === episodeId) {
                waveData = null;
            }
        } finally {
            if (episode?.id === episodeId) {
                isLoadingWave = false;
            }
        }
    }

    let audio;
    let visual = true;
    let muted;
    let volume_knob = 50;
    let playback_knob = 100;
    let duration = 0;

    let lastSavedTime = 0;

    // Key with an explicit prefix and stringified ID/number
    $: storageKey =
        episode && episode.number != null ? `ep_time_${episode.number}` : null;

    function restoreSavedTime() {
        if (isRadio || !storageKey || !audio) return;
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const parsedTime = parseFloat(saved);
            if (Number.isFinite(parsedTime) && parsedTime < audio.duration) {
                audio.currentTime = parsedTime;
                $current_time = parsedTime;
            }
        }
    }

    function handleTimeUpdate() {
        if (!audio || isSeeking || isRadio) return;
        $current_time = audio.currentTime;

        // Throttle save: only write to localStorage every 3-5 seconds
        if (storageKey && Math.abs(audio.currentTime - lastSavedTime) > 3) {
            lastSavedTime = audio.currentTime;
            localStorage.setItem(storageKey, audio.currentTime.toString());
        }
    }

    // --- REFACTORED & FIXED ---
    let isSeeking = false; // State to manage drag operations
    let wasPaused = true; // State to remember playback status before seeking
    let waveContainer; // bind:this to the waveform container

    $: cursorPosition = duration > 0 ? ($current_time / duration) * 100 : 0;
    $: volume = volume_knob / 100;
    $: playbackRate = playback_knob / 100;

    // Derived properties for navigation
    $: isFirstEpisode = isRadio || $current_episode === $episodes.length - 1;
    $: isLastEpisode = isRadio || $current_episode === 0;
    $: prevLink = !isFirstEpisode
        ? $episodes[$current_episode + 1].link
        : undefined;
    $: nextLink = !isLastEpisode
        ? $episodes[$current_episode - 1].link
        : undefined;

    function playAudio() {
        isSeeking = false;
        if ($paused) {
            if (isRadio) {
                audio.src = `${episode.episode}?cb=${new Date().getTime()}`;
                audio.load();
            }
            audio.play();
        } else {
            audio.pause();
        }
    }

    function forwardAudio() {
        audio.currentTime += 15;
    }

    function backwardAudio() {
        audio.currentTime -= 15;
    }

    function toggleDesc() {
        $episode_desc = !$episode_desc;
    }

    function toggleVisual() {
        visual = !visual;
    }

    function handleSeekStart(e) {
        if (e.type == "seekstart") e = e.detail;
        e.preventDefault();
        isSeeking = true;

        wasPaused = $paused;
        if (!wasPaused) {
            audio.pause();
        }
        updateSeekPosition(e);
    }

    function handleSeekMove(e) {
        if (!isSeeking) return;
        updateSeekPosition(e);
    }

    function handleSeekEnd(e) {
        if (!isSeeking) return;
        isSeeking = false;
        if (!wasPaused) {
            audio.play();
        }
    }

    function updateSeekPosition(e) {
        if (!duration || !isFinite(duration) || !waveContainer) return 0;

        const rect = waveContainer.getBoundingClientRect();
        const clientX = e.changedTouches
            ? e.changedTouches[0].clientX
            : e.clientX;
        let x = clientX - rect.left;
        x = Math.max(0, Math.min(x, rect.width));

        const newTime = (x / rect.width) * duration;
        audio.currentTime = newTime;
        $current_time = newTime;
    }
</script>

<svelte:window
    on:mousemove={handleSeekMove}
    on:mouseup={handleSeekEnd}
    on:touchmove={handleSeekMove}
    on:touchend={handleSeekEnd}
/>

<div
    class="drop-shadow-md shadow-md shadow-blue-500 bg-neutral mb-8 xl:grid xl:grid-cols-3 xl:grid-rows-3 gap-2 h-auto xl:h-80 bg-repeat"
    id="audio-player"
>
    <audio
        id="audio"
        crossorigin="anonymous"
        src={episode.episode}
        bind:this={audio}
        bind:muted
        bind:paused={$paused}
        bind:volume
        bind:duration
        bind:playbackRate
        on:timeupdate={handleTimeUpdate}
        on:loadedmetadata={restoreSavedTime}
    />

    <figure
        class="screen border-2 border-black xl:ml-2 xl:mb-2 xl:mt-2 shadow-inner shadow-black bg-stone-500 h-64 xl:h-auto xl:row-span-4"
    >
        <img
            class="max-w-full w-full h-full object-contain p-2"
            alt="episode cover"
            src={episode.image}
        />
    </figure>

    <div
        class="screen bg-stone-500 h-full self-start shadow-inner border-2 mt-2 border-black p-4 shadow-black text-primary-content"
    >
        <p>
            {episode.title}
        </p>
    </div>
    <div class="flex justify-end mr-2 gap-2">
        <div
            class="screen flex-grow block h-16 xl:h-full bg-stone-500 shadow-inner mt-2 border-2 border-black shadow-black self-start xl:align-bottom"
        >
            {#if !$paused && visual}
                <AudioBars bind:audio />
            {/if}
        </div>
        <AudioButton on:click={toggleVisual} toggle={visual}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="icon icon-tabler icon-tabler-activity justify-center self-center"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="#000000"
                fill="none"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M3 12h4l3 8l4 -16l3 8h4" />
            </svg>
        </AudioButton>
    </div>
    <div
        bind:this={waveContainer}
        class="screen relative h-16 xl:h-3/4 mt-2 bg-stone-500 self-end shadow-inner border-2 border-black shadow-black col-span-2 flex items-end justify-end xl:mr-2"
        on:mousedown={!isRadio ? handleSeekStart : null}
        on:touchstart={!isRadio ? handleSeekStart : null}
    >
        {#if isRadio}
            <!-- Radio placeholder visual -->
            <div
                class="absolute inset-0 flex items-center justify-center opacity-50"
            >
                <span
                    class="text-4xl tracking-widest text-fuchsia-400 animate-pulse"
                    >FNORD</span
                >
            </div>
        {:else}
            {#if waveData}
                <AudioWave wave={waveData} />
            {:else if isLoadingWave}
                <div
                    class="absolute inset-0 flex items-center justify-center opacity-40"
                >
                    <span
                        class="text-xs tracking-wider uppercase text-neutral animate-pulse"
                        >Loading wave...</span
                    >
                </div>
            {/if}

            <AudioCursor
                position={cursorPosition}
                on:seekstart={handleSeekStart}
            />
        {/if}
        <p
            class:blink={$paused}
            class="z-10 p-1 pointer-events-none text-primary-content"
        >
            {#if isRadio}
                <span class="text-red-500 font-bold">● LIVE</span>
            {:else}
                <AudioTime bind:duration bind:currentTime={$current_time} />
            {/if}
        </p>
    </div>
    <div class="flex col-span-2 justify-around p-2 mt-2 flex-wrap">
        <div class="flex">
            <div class="pb-2 pt-2">
                <AudioButton
                    on:click={toggleDesc}
                    toggle={$episode_desc}
                    inactive={isRadio}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-details justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#000000"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path
                            d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"
                        />
                        <path d="M12 3v16" />
                    </svg>
                </AudioButton>
            </div>
            <a class="pb-2 pt-2" href={episode.episode} download>
                <AudioButton inactive={isRadio}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-player-record justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#ef4444"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <circle cx="12" cy="12" r="7" />
                    </svg>
                </AudioButton>
            </a>
        </div>

        <div class="flex">
            <a class="pb-2 pt-2" href={prevLink}>
                <AudioButton inactive={isFirstEpisode}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-player-skip-back justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#000000"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M20 5v14l-12 -7z" />
                        <line x1="4" y1="5" x2="4" y2="19" />
                    </svg>
                </AudioButton>
            </a>
            <div class="pb-2 pt-2">
                <AudioButton on:click={backwardAudio}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-player-track-prev justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#000000"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M21 5v14l-8 -7z" />
                        <path d="M10 5v14l-8 -7z" />
                    </svg>
                </AudioButton>
            </div>
            <div class="pb-2 pt-2">
                <AudioButton
                    on:click={playAudio}
                    toggle={$paused}
                    reverse={true}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-player-play justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#000000"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M7 4v16l13 -8z" />
                    </svg>
                </AudioButton>
            </div>

            <div class="pb-2 pt-2">
                <AudioButton on:click={forwardAudio}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-player-track-next justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#000000"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M3 5v14l8 -7z" />
                        <path d="M14 5v14l8 -7z" />
                    </svg>
                </AudioButton>
            </div>
            <a class="pb-2 pt-2" href={nextLink}>
                <AudioButton inactive={isLastEpisode}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="icon icon-tabler icon-tabler-player-skip-forward justify-center self-center"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                        stroke="#000000"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                        <path d="M4 5v14l12 -7z" />
                        <line x1="20" y1="5" x2="20" y2="19" />
                    </svg>
                </AudioButton>
            </a>
        </div>

        <div class="flex justify-between m-8 xl:m-0">
            <Knob
                size={50}
                label="volume"
                degrees={260}
                tickCount={15}
                min={0}
                max={100}
                bind:value={volume_knob}
            />
            {#if !isRadio}
                <Knob
                    size={50}
                    label="speed"
                    degrees={260}
                    tickCount={6}
                    min={75}
                    max={200}
                    bind:value={playback_knob}
                />
            {/if}
        </div>
    </div>
</div>

<style>
    .blink {
        -webkit-animation: 1.5s linear infinite condemned_blink_effect; /* for Safari 4.0 - 8.0 */
        animation: 1.5s linear infinite condemned_blink_effect;
    }

    /* for Safari 4.0 - 8.0 */
    @-webkit-keyframes condemned_blink_effect {
        0% {
            visibility: hidden;
        }
        50% {
            visibility: hidden;
        }
        100% {
            visibility: visible;
        }
    }

    @keyframes condemned_blink_effect {
        0% {
            visibility: hidden;
        }
        50% {
            visibility: hidden;
        }
        100% {
            visibility: visible;
        }
    }

    .screen {
        background-image: linear-gradient(
                0deg,
                transparent 24%,
                rgba(255, 255, 255, 0.05) 25%,
                rgba(255, 255, 255, 0.05) 26%,
                transparent 27%,
                transparent 74%,
                rgba(255, 255, 255, 0.05) 75%,
                rgba(255, 255, 255, 0.05) 76%,
                transparent 77%,
                transparent
            ),
            linear-gradient(
                90deg,
                transparent 24%,
                rgba(255, 255, 255, 0.05) 25%,
                rgba(255, 255, 255, 0.05) 26%,
                transparent 27%,
                transparent 74%,
                rgba(255, 255, 255, 0.05) 75%,
                rgba(255, 255, 255, 0.05) 76%,
                transparent 77%,
                transparent
            );
        background-size: 50px 50px;
    }
</style>
